import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useAuth } from '../app/auth-context'
import { OptionList } from '../components/wizard/OptionList'
import { SkeletonCards } from '../components/ui/Skeleton'
import { WizardShell } from '../components/wizard/WizardShell'
import { CarIcon, ChevronIcon } from '../components/icons'
import { Button } from '../components/ui/Button'
import { ErrorNote } from '../components/ui/ErrorNote'
import {
  selectClass,
  useVehicleDraft,
  VEHICLE_STEP_HINTS,
  VEHICLE_STEP_TITLES,
  type VehicleStepId,
} from '../components/vehicle/useVehicleDraft'
import { ApiError } from '../lib/api'
import { toPersianDigits } from '../lib/fa'
import { tehranTodayIso } from '../lib/jalali'
import { createQuote, type ReferenceItem } from '../lib/quotes-api'
import { getVehicles, saveVehicle, type SavedVehicle } from '../lib/vehicles-api'

const MAX_BODILY_DISCOUNT_YEARS = 14
const MAX_PROPERTY_DISCOUNT_YEARS = 8

/**
 * The statutory minimum تعهد مالی — what the official base premium already buys. Every quote
 * starts here; raising it is a choice made on the comparison screen, where its price is visible,
 * not a question asked blind before any price has been seen.
 */
export const DEFAULT_PROPERTY_TIER = 'P_2_5'

type StepId = 'garage' | VehicleStepId | 'history'

/**
 * The shortest road to prices: which car, then the one per-policy question that moves the price
 * most (no-claims history). Everything else has a default and is changed later, next to the
 * number it changes:
 *
 * - **Start date** is today. Nearly everyone wants cover now; «later» is an option at checkout.
 * - **تعهد مالی** is the statutory minimum, raised on the comparison screen.
 *
 * A saved car skips straight past the vehicle screens — re-asking them is the whole point of
 * having saved it.
 */
export function MotorWizardPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { status } = useAuth()
  const signedIn = status === 'authenticated'
  const vehicle = useVehicleDraft()

  /*
   * The current screen is held by **id**, not by index. An index into a list that changes length
   * is a bug waiting for a motorcycle: the list shrinks by one when one is picked, and an index
   * would silently land on the screen after the one intended.
   */
  const [stepId, setStepId] = useState<StepId>('garage')
  /** `saved` once a garage car is applied; `new` once the customer chose to add another. */
  const [source, setSource] = useState<'saved' | 'new' | null>(null)
  const [picked, setPicked] = useState<SavedVehicle | null>(null)

  const [hasPrevious, setHasPrevious] = useState<boolean | null>(null)
  const [bodilyYears, setBodilyYears] = useState(0)
  const [propertyYears, setPropertyYears] = useState(0)

  /*
   * Quoting stays open to anyone, so the saved list is only fetched for a signed-in visitor —
   * and `enabled` alone would still serve a previous session's cache, hence the `signedIn`
   * guard on the read as well.
   */
  const vehicles = useQuery({ queryKey: ['vehicles'], queryFn: getVehicles, enabled: signedIn })
  const saved = signedIn ? (vehicles.data ?? []) : []
  const hasGarage = saved.length > 0

  const steps: StepId[] =
    hasGarage && source !== 'new'
      ? ['garage', 'history']
      : [...(hasGarage ? (['garage'] as const) : []), ...vehicle.steps, 'history']

  const applySaved = (car: SavedVehicle) => {
    vehicle.load(car)
    setPicked(car)
    setSource('saved')
    setStepId('history')
  }

  // Arriving from «استعلام» on the saved list: prefill once the list has loaded.
  const requestedId = (location.state as { vehicleId?: string } | null)?.vehicleId
  const [appliedId, setAppliedId] = useState<string | null>(null)
  if (requestedId && requestedId !== appliedId && saved.length > 0) {
    const match = saved.find((v) => v.id === requestedId)
    setAppliedId(requestedId)
    if (match) applySaved(match)
  }

  /*
   * Falls back to the first screen when the current id is not in the list — 'garage' for a
   * visitor with no saved cars, or 'usage' right after picking a motorcycle.
   */
  const index = Math.max(0, steps.indexOf(stepId))
  const step = steps[index] as StepId
  const isLast = index === steps.length - 1
  const goTo = (next: number) => setStepId(steps[next] as StepId)

  const quote = useMutation({
    mutationFn: () =>
      createQuote('motor-tpl', {
        vehicleUsage: vehicle.effectiveUsage,
        vehicleGroup: vehicle.group,
        vehicleModelId: vehicle.modelId,
        productionYear: Number(vehicle.productionYear),
        plate: vehicle.plate,
        startDate: tehranTodayIso(),
        hasPreviousPolicy: hasPrevious ?? false,
        bodilyDiscountYears: hasPrevious ? bodilyYears : 0,
        propertyDiscountYears: hasPrevious ? propertyYears : 0,
        propertyCoverageTier: DEFAULT_PROPERTY_TIER,
      }),
    onSuccess: (created) => {
      /*
       * A new car is registered as a side effect of quoting it. Saving is a convenience, not
       * part of the purchase: it must never delay the prices or fail the quote the customer just
       * waited for. Fire-and-forget, and a failure costs them one retyped plate next time.
       */
      if (signedIn && source !== 'saved' && vehicle.modelId) {
        void saveVehicle(vehicle.body())
          .then(() => queryClient.invalidateQueries({ queryKey: ['vehicles'] }))
          .catch(() => undefined)
      }
      navigate(`/quotes/${created.id}`, { replace: true })
    },
  })

  const complete = (id: StepId): boolean =>
    id === 'garage' ? false : id === 'history' ? hasPrevious !== null : vehicle.complete[id]

  const error = quote.error instanceof ApiError ? quote.error : undefined
  const fieldMessages = error?.fields ? Object.values(error.fields) : []

  // Until a signed-in visitor's garage is known, the first screen is not either.
  const resolvingGarage = signedIn && vehicles.isPending

  return (
    <WizardShell
      title={step === 'garage' ? 'برای کدام خودرو؟' : TITLES[step]}
      hint={step === 'garage' ? undefined : HINTS[step]}
      step={index + 1}
      totalSteps={steps.length}
      onBack={() => (index === 0 ? navigate('/') : goTo(index - 1))}
      footer={
        step === 'garage' ? undefined : (
          <>
            <Button
              disabled={!complete(step) || quote.isPending}
              loading={quote.isPending}
              onClick={() => (isLast ? quote.mutate() : goTo(index + 1))}
            >
              {isLast ? 'مقایسه قیمت‌ها' : 'ادامه'}
            </Button>
            {error && fieldMessages.length === 0 ? <ErrorNote>{error.messageFa}</ErrorNote> : null}
            {fieldMessages.map((message) => (
              <ErrorNote key={message}>{message}</ErrorNote>
            ))}
          </>
        )
      }
    >
      {resolvingGarage ? (
        <SkeletonCards count={3} height="h-14" className="space-y-2.5" />
      ) : step === 'garage' ? (
        <div className="space-y-2.5">
          {saved.map((car) => (
            <button
              key={car.id}
              type="button"
              onClick={() => applySaved(car)}
              className="flex w-full items-center gap-3 rounded-2xl border border-line bg-card px-4 py-3 text-right active:scale-[0.99]"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-300">
                <CarIcon className="h-6 w-6" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-strong">
                  {car.modelLabelFa}
                </span>
                <span className="mt-0.5 block text-xs text-muted">
                  {car.plateFa} · مدل {toPersianDigits(car.productionYear)}
                </span>
              </span>
              <ChevronIcon className="h-4 w-4 shrink-0 text-muted" />
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setSource('new')
              setStepId('model')
            }}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-line px-4 py-3.5 text-sm font-medium text-brand-600 dark:text-brand-300"
          >
            + ثبت خودروی دیگر
          </button>
        </div>
      ) : step === 'history' ? (
        <div className="space-y-5">
          {source === 'saved' && picked ? (
            <p className="rounded-2xl bg-sunken px-4 py-3 text-xs leading-6 text-muted">
              {picked.modelLabelFa} · {picked.plateFa}
            </p>
          ) : null}

          <OptionList
            options={PREVIOUS_POLICY_OPTIONS}
            value={hasPrevious === null ? null : hasPrevious ? 'yes' : 'no'}
            onChange={(next) => {
              const yes = next === 'yes'
              setHasPrevious(yes)
              // Leaving stale years behind would send a discount the customer just said they
              // do not have — which the API rejects, from a screen that looks answered.
              if (!yes) {
                setBodilyYears(0)
                setPropertyYears(0)
              }
            }}
          />

          {hasPrevious ? (
            <div className="space-y-5 rounded-2xl border border-line bg-card p-4">
              <YearsSelect
                label="سال‌های عدم خسارت جانی"
                max={MAX_BODILY_DISCOUNT_YEARS}
                value={bodilyYears}
                onChange={setBodilyYears}
              />
              <YearsSelect
                label="سال‌های عدم خسارت مالی"
                max={MAX_PROPERTY_DISCOUNT_YEARS}
                value={propertyYears}
                onChange={setPropertyYears}
              />
              <p className="text-xs leading-6 text-muted">
                این عددها روی بیمه‌نامه قبلی شما نوشته شده است. اگر مطمئن نیستید، صفر بگذارید —
                تخفیف هنگام صدور از سامانه سنهاب بررسی می‌شود.
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        vehicle.render(step)
      )}
    </WizardShell>
  )
}

const TITLES: Record<Exclude<StepId, 'garage'>, string> = {
  ...VEHICLE_STEP_TITLES,
  history: 'بیمه‌نامه شخص ثالث قبلی دارید؟',
}

const HINTS: Record<Exclude<StepId, 'garage'>, string> = {
  ...VEHICLE_STEP_HINTS,
  history: 'سابقه عدم خسارت، بزرگ‌ترین عامل کاهش حق بیمه است.',
}

const PREVIOUS_POLICY_OPTIONS: ReferenceItem[] = [
  { value: 'yes', labelFa: 'بله، بیمه‌نامه قبلی دارم' },
  { value: 'no', labelFa: 'خیر، اولین بیمه‌نامه من است' },
]

function YearsSelect({
  label,
  max,
  value,
  onChange,
}: {
  label: string
  max: number
  value: number
  onChange: (next: number) => void
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-strong">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className={selectClass}
      >
        {Array.from({ length: max + 1 }, (_, years) => (
          <option key={years} value={years}>
            {years === 0 ? 'بدون سابقه' : `${toPersianDigits(years)} سال`}
          </option>
        ))}
      </select>
    </label>
  )
}
